import { useCallback, useEffect, useState } from "react";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES, formatMoney, getCategoryLabel } from "@/lib/currency";
import Modal from "@/components/Modal";

function budgetStatus(percent) {
  const pct = percent || 0;
  if (pct >= 100) return "over";
  if (pct >= 80) return "warn";
  return "ok";
}
function statusBarColor(status) {
  if (status === "over") return "bg-red-400";
  if (status === "warn") return "bg-yellow-300";
  return "bg-emerald-300";
}
function statusTextColor(status) {
  if (status === "over") return "text-red-700";
  if (status === "warn") return "text-yellow-800";
  return "text-emerald-700";
}

function BudgetCard({ b, currency, onDelete }) {
  const status = budgetStatus(b.percent);
  const pctCapped = Math.min(b.percent || 0, 100);
  const barClass = statusBarColor(status);
  const textClass = statusTextColor(status);
  const statusLabel = status === "over" ? "Over budget!" : `${b.percent || 0}% used`;

  return (
    <div className="brutal-card p-5" data-testid={`budget-card-${b.category}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-black/50">Category</div>
          <div className="font-outfit font-black text-xl">{getCategoryLabel(b.category)}</div>
        </div>
        <button onClick={() => onDelete(b.id)} className="w-8 h-8 border-2 border-black rounded-lg bg-white hover:bg-red-200 flex items-center justify-center" data-testid={`delete-budget-${b.category}`}>
          <Trash size={14} weight="bold" />
        </button>
      </div>

      <div className="flex items-end justify-between mb-2">
        <div className="font-outfit font-black text-2xl">{formatMoney(b.spent, currency)}</div>
        <div className="text-sm font-manrope text-black/60">of {formatMoney(b.limit_in_base, currency)}</div>
      </div>

      <div className="h-4 bg-white border-2 border-black rounded-full overflow-hidden brutal-shadow-sm">
        <div className={`h-full ${barClass}`} style={{ width: `${pctCapped}%`, transition: "width 400ms ease" }} />
      </div>
      <div className={`text-xs mt-2 font-bold uppercase tracking-widest ${textClass}`}>{statusLabel}</div>
    </div>
  );
}

function BudgetModal({ open, onClose, onSaved }) {
  const { currency } = useAuth();
  const [category, setCategory] = useState("food");
  const [amount, setAmount] = useState("");
  const [ccy, setCcy] = useState(currency);
  const [currencies, setCurrencies] = useState([currency]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAmount("");
    setCategory("food");
    setCcy(currency);
  }, [open, currency]);

  useEffect(() => {
    api.get("/currencies")
      .then(({ data }) => setCurrencies(data.map((c) => c.code)))
      .catch((err) => console.error("Failed to load currencies:", err));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/budgets", { category, monthly_limit: parseFloat(amount), currency: ccy });
      toast.success("Budget saved");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Set a monthly budget" testId="budget-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="budget-form">
        <div>
          <label className="text-xs font-bold uppercase tracking-widest block mb-1">Category</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="brutal-input" data-testid="budget-category-select">
            {CATEGORIES.expense.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest block mb-1">Monthly limit</label>
            <input type="number" step="0.01" min="0" required value={amount} onChange={(e) => setAmount(e.target.value)}
              className="brutal-input" placeholder="0.00" data-testid="budget-amount-input" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest block mb-1">Currency</label>
            <select value={ccy} onChange={(e) => setCcy(e.target.value)} className="brutal-input" data-testid="budget-currency-select">
              {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <button type="submit" disabled={saving} className="brutal-btn bg-violet-300 hover:bg-violet-400 w-full py-2.5 disabled:opacity-50" data-testid="budget-submit-btn">
          {saving ? "Saving…" : "Save budget"}
        </button>
      </form>
    </Modal>
  );
}

export default function Budgets() {
  const { currency } = useAuth();
  const [budgets, setBudgets] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/budgets");
      setBudgets(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [currency, load]);

  const remove = async (id) => {
    try {
      await api.delete(`/budgets/${id}`);
      setBudgets((b) => b.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="budgets-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-outfit font-black text-4xl sm:text-5xl tracking-tight">Budgets</h1>
          <p className="font-manrope text-black/60 mt-1">Set monthly limits per category.</p>
        </div>
        <button onClick={() => setOpen(true)} className="brutal-btn bg-violet-300 hover:bg-violet-400 px-5 py-2.5 flex items-center gap-2" data-testid="new-budget-btn">
          <Plus size={18} weight="bold" /> New budget
        </button>
      </div>

      {loading && <div className="brutal-card p-6 font-manrope">Loading…</div>}
      {!loading && budgets.length === 0 && (
        <div className="brutal-card p-12 text-center">
          <div className="font-outfit font-black text-2xl mb-2">No budgets yet</div>
          <p className="font-manrope text-black/50 mb-4">Set a monthly limit for your biggest expense category to start.</p>
          <button onClick={() => setOpen(true)} className="brutal-btn bg-violet-300 hover:bg-violet-400 px-5 py-2">Create one</button>
        </div>
      )}
      {!loading && budgets.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="budgets-list">
          {budgets.map((b) => (
            <BudgetCard key={b.id} b={b} currency={currency} onDelete={remove} />
          ))}
        </div>
      )}

      <BudgetModal open={open} onClose={() => setOpen(false)} onSaved={load} />
    </div>
  );
}
